import React, { useState, useEffect } from 'react';
import {
	Button,
	Tag,
	Modal,
	Divider,
	Typography,
	Checkbox,
	Input,
	notification,
	Tooltip,
} from 'antd';
import { EyeOutlined, MessageOutlined } from '@ant-design/icons';
import { FormGroup } from '@/types/TInbox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateIntakeResponse } from '@/api/logs';

const { Text } = Typography;

interface IntakeFormsDisplayProps {
	formGroup: FormGroup;
	agentId: string;
}

const IntakeFormsDisplay: React.FC<IntakeFormsDisplayProps> = ({ formGroup, agentId }) => {
	const [transcriptModalVisible, setTranscriptModalVisible] = useState(false);
	const [selectedFormResponse, setSelectedFormResponse] = useState<any>(null);

	const [responses, setResponses] = useState<any[]>([]);
	const [notes, setNotes] = useState<{ [key: string]: string }>({});
	const queryClient = useQueryClient();
	const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
	const [editingResponse, setEditingResponse] = useState<any>(null);
	const [currentNote, setCurrentNote] = useState('');

	const updateResponseMutation = useMutation({
		mutationFn: updateIntakeResponse,
		onMutate: async ({ responseId, is_completed, note }) => {
			setResponses((prev) =>
				prev.map((res) =>
					res.id === responseId
						? { ...res, completed_at: is_completed ? new Date().toISOString() : null, note }
						: res,
				),
			);

			await new Promise((resolve) => setTimeout(resolve, 1000));

			setResponses((currentResponses) =>
				[...currentResponses].sort((a, b) => {
					const aCompleted = !!a.completed_at;
					const bCompleted = !!b.completed_at;
					if (aCompleted && !bCompleted) {
						return 1;
					}
					if (!aCompleted && bCompleted) {
						return -1;
					}
					return new Date(b.date).getTime() - new Date(a.date).getTime();
				}),
			);
		},
		onError: () => {
			setResponses(formGroup.responses);
			notification.error({ message: 'Update Failed', description: 'Could not save changes.' });
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ['callLogs'] });
		},
	});

	useEffect(() => {
		const sorted = [...formGroup.responses].sort((a, b) => {
			const aCompleted = !!a.completed_at;
			const bCompleted = !!b.completed_at;
			if (aCompleted && !bCompleted) {
				return 1;
			}
			if (!aCompleted && bCompleted) {
				return -1;
			}
			return new Date(b.date).getTime() - new Date(a.date).getTime();
		});
		setResponses(sorted);

		const initialNotes = formGroup.responses.reduce(
			(acc, res) => {
				acc[res.id] = res.note || '';
				return acc;
			},
			{} as { [key: string]: string },
		);
		setNotes(initialNotes);
	}, [formGroup.responses]);

	const handleCompletionChange = (response: any, checked: boolean) => {
		updateResponseMutation.mutate({
			agentId,
			responseId: response.id,
			is_completed: checked,
			note: notes[response.id] || '',
		});
	};

	const handleOpenNoteModal = (response: any) => {
		setEditingResponse(response);
		setCurrentNote(notes[response.id] || '');
		setIsNoteModalVisible(true);
	};

	const handleCancelNoteModal = () => {
		setIsNoteModalVisible(false);
		setEditingResponse(null);
		setCurrentNote('');
	};

	const handleSaveNote = () => {
		if (!editingResponse) {
			return;
		}
		setNotes((prev) => ({ ...prev, [editingResponse.id]: currentNote }));
		updateResponseMutation.mutate(
			{
				agentId,
				responseId: editingResponse.id,
				is_completed: !!editingResponse.completed_at,
				note: currentNote,
			},
			{
				onSuccess: () => {
					handleCancelNoteModal();
				},
			},
		);
	};

	const formatTimestamp = (dateString?: string | null) => {
		if (!dateString) {
			return null;
		}
		return new Date(dateString).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
		});
	};

	const parseTranscript = (transcript?: string) => {
		if (!transcript) {
			return [];
		}
		const lines = transcript.split('\n');
		return lines
			.map((line) => {
				const match = line.match(/^(assistant|user)@\s*(.+)$/);
				if (match) {
					return {
						speaker: match[1] === 'assistant' ? 'AI' : 'Customer',
						message: match[2],
					};
				}
				return null;
			})
			.filter(Boolean);
	};

	const handleViewTranscript = (response: any) => {
		setSelectedFormResponse(response);
		setTranscriptModalVisible(true);
	};

	return (
		<div className="h-full">
			{/* Show responses table directly - NO intermediate screen */}
			<div className="h-full grid grid-rows-[auto_1fr] gap-y-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-3">
						<span className="font-semibold text-gray-900">{formGroup.title} Responses</span>
					</div>
					<Tag color="blue">{formGroup.responses.length} Total</Tag>
				</div>

				<div className="overflow-auto bg-white rounded-lg border border-gray-200">
					<table className="w-full">
						<thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
							<tr>
								{formGroup.questions.map((question: string, index: number) => (
									<th
										key={index}
										className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase"
									>
										<div className="flex items-center space-x-1">
											<span className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full text-xs font-bold flex items-center justify-center">
												{index + 1}
											</span>
											<span>
												{question.length > 25 ? question.substring(0, 25) + '...' : question}
											</span>
										</div>
									</th>
								))}
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
									Date
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
									Phone
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
									Action
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
									Completed
								</th>

								<th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
									Notes
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{responses.map((response: any) => (
								<tr
									key={response.id}
									className={`transition-colors duration-300 ${response.completed_at ? 'bg-gray-100 text-gray-600' : 'hover:bg-gray-50'}`}
								>
									{formGroup.questions.map((question: string, qIndex: number) => (
										<td key={qIndex} className="px-4 py-3 text-sm">
											{response.answers[question] ? (
												<span className="text-gray-900">{response.answers[question]}</span>
											) : (
												<span className="text-gray-400 italic">No response</span>
											)}
										</td>
									))}
									<td className="px-4 py-3 text-sm text-gray-600">{formatDate(response.date)}</td>
									<td className="px-4 py-3 text-sm text-gray-900">{response.phone}</td>
									<td className="px-4 py-3 text-sm">
										<Button
											type="primary"
											size="small"
											icon={<EyeOutlined />}
											onClick={() => handleViewTranscript(response)}
											className="bg-blue-600 border-blue-600"
										>
											View Full Conversation
										</Button>
									</td>
									<td className="px-4 py-3">
										<Checkbox
											checked={!!response.completed_at}
											onChange={(e) => handleCompletionChange(response, e.target.checked)}
											disabled={
												updateResponseMutation.isPending &&
												updateResponseMutation.variables?.responseId === response.id
											}
										/>
										{response.completed_at && (
											<div className="text-xs text-gray-400 mt-1 italic">
												{formatTimestamp(response.completed_at)}
											</div>
										)}
									</td>
									<td className="px-4 py-3">
										<Tooltip title={notes[response.id]} placement="top">
											<Button size="small" onClick={() => handleOpenNoteModal(response)}>
												{notes[response.id] ? 'Edit Note' : 'Add Note'}
											</Button>
										</Tooltip>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{/* Transcript Modal */}
			<Modal
				title={
					<div className="flex items-center space-x-3">
						<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
							<MessageOutlined className="text-blue-600" />
						</div>
						<div>
							<div className="font-semibold text-gray-900">Conversation</div>
							<div className="text-sm text-gray-500 font-normal">
								{selectedFormResponse?.phone} •{' '}
								{selectedFormResponse?.date && formatDate(selectedFormResponse.date)}
							</div>
						</div>
					</div>
				}
				open={transcriptModalVisible}
				onCancel={() => {
					setTranscriptModalVisible(false);
					setSelectedFormResponse(null);
				}}
				footer={[
					<Button
						key="close"
						onClick={() => {
							setTranscriptModalVisible(false);
							setSelectedFormResponse(null);
						}}
						className="rounded-lg"
					>
						Close
					</Button>,
				]}
				width={700}
			>
				{selectedFormResponse && (
					<div className="space-y-4">
						{/* Conversation */}
						<div className="max-h-96 overflow-y-auto bg-gray-50 p-4 rounded-lg border space-y-3">
							{selectedFormResponse.transcript ? (
								parseTranscript(selectedFormResponse.transcript).map(
									(entry: any, index: number) => (
										<div
											key={index}
											className={`flex ${entry?.speaker === 'AI' ? 'justify-start' : 'justify-end'}`}
										>
											<div className="max-w-md">
												<div
													className={`flex items-start space-x-2 ${entry?.speaker === 'AI' ? '' : 'flex-row-reverse space-x-reverse'}`}
												>
													<div
														className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
															entry?.speaker === 'AI'
																? 'bg-blue-500 text-white'
																: 'bg-green-500 text-white'
														}`}
													>
														{entry?.speaker === 'AI' ? 'AI' : 'U'}
													</div>
													<div
														className={`rounded-lg p-3 ${
															entry?.speaker === 'AI'
																? 'bg-white border border-gray-200'
																: 'bg-green-500 text-white'
														}`}
													>
														<p
															className={`text-sm leading-relaxed ${entry?.speaker === 'AI' ? 'text-gray-800' : 'text-white'}`}
														>
															{entry.message}
														</p>
													</div>
												</div>
											</div>
										</div>
									),
								)
							) : (
								<div className="text-center py-8">
									<MessageOutlined className="text-4xl text-gray-400 mb-4" />
									<Text className="text-gray-500">
										No transcript available for this conversation
									</Text>
								</div>
							)}
						</div>

						{/* Form Responses Summary */}
						<Divider orientation="left" className="!my-6">
							<span className="text-sm font-medium text-gray-600">Form Responses</span>
						</Divider>

						<div className="space-y-3">
							{Object.entries(selectedFormResponse.answers || {}).map(
								([question, answer], index) => (
									<div
										key={index}
										className="border-l-4 border-blue-400 pl-4 bg-blue-50 p-3 rounded-r-lg"
									>
										<div className="mb-2">
											<p className="text-sm font-medium text-gray-900 leading-relaxed">
												{question}
											</p>
										</div>
										<p className="text-sm text-gray-700 leading-relaxed">
											{answer ? (
												String(answer)
											) : (
												<span className="text-gray-400 italic">No response provided</span>
											)}
										</p>
									</div>
								),
							)}
						</div>
					</div>
				)}
			</Modal>

			{/* Notes Modal */}
			<Modal
				title={`Note for ${editingResponse?.phone}`}
				open={isNoteModalVisible}
				onCancel={handleCancelNoteModal}
				footer={[
					<Button key="back" onClick={handleCancelNoteModal}>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						loading={updateResponseMutation.isPending}
						onClick={handleSaveNote}
						disabled={!currentNote.trim() || updateResponseMutation.isPending}
					>
						Save Note
					</Button>,
				]}
			>
				<Input.TextArea
					rows={6}
					value={currentNote}
					onChange={(e) => setCurrentNote(e.target.value)}
					placeholder="Add internal notes here..."
				/>
			</Modal>
		</div>
	);
};

export default IntakeFormsDisplay;
